import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Card, Button, Input, Select, Textarea, Label, Empty } from "@/components/Primitives";
import { StatusPill, ticketStatusVariant } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import { Plus, Send, X, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function TicketsPage() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const [showNew, setShowNew] = useState(false);
  const loc = useLocation();

  const load = async () => {
    const { data } = await api.get("/tickets");
    setTickets(data || []);
    if (sel) {
      const t = data?.find((x) => x.id === sel.id);
      if (t) setSel(t);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (new URLSearchParams(loc.search).get("new") === "1") setShowNew(true);
  }, [loc.search]);

  const sendReply = async () => {
    if (!reply.trim() || !sel) return;
    await api.post(`/tickets/${sel.id}/reply`, { body: reply });
    setReply("");
    await load();
  };

  const setStatus = async (s) => {
    if (!sel) return;
    await api.patch(`/tickets/${sel.id}`, { status: s });
    await load();
  };

  const deleteTicket = async () => {
    if (!sel) return;
    if (!window.confirm(`Delete ticket ${sel.number} "${sel.subject}"? This also removes it from Firestore.`)) return;
    await api.delete(`/tickets/${sel.id}`);
    toast.success("Ticket deleted");
    setSel(null);
    await load();
  };

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumb={user?.role === "customer" ? "My account · Support" : "Support · Tickets"}
        title={user?.role === "customer" ? "Support" : "Support queue"}
        subtitle={user?.role === "customer" ? "We're here when you need us." : "Everything customers are asking."}
        actions={<Button onClick={() => setShowNew(true)} data-testid="new-ticket-btn"><Plus className="w-4 h-4" strokeWidth={2}/>New ticket</Button>}
      />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <Card className="lg:col-span-2 p-0" title={`${tickets.length} tickets`}>
          <div className="divide-y divide-[hsl(var(--border))] max-h-[70vh] overflow-auto">
            {tickets.map((t) => (
              <button key={t.id} onClick={() => setSel(t)} className={`w-full text-left px-5 py-3 hover:bg-[hsl(var(--surface-hover))] ${sel?.id === t.id ? "bg-[hsl(var(--primary-muted))]" : ""}`} data-testid="ticket-row">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-semibold truncate">{t.subject}</div>
                    <div className="text-[11px] text-[hsl(var(--text-muted))]">{t.number} · {t.customer_name}</div>
                  </div>
                  <StatusPill variant={ticketStatusVariant(t.status)}>{t.status.replace("_", " ")}</StatusPill>
                </div>
              </button>
            ))}
            {tickets.length === 0 && <Empty title="No tickets" />}
          </div>
        </Card>

        <Card className="lg:col-span-3 p-0">
          {!sel ? (
            <div className="h-full flex items-center justify-center py-16"><Empty title="Select a ticket" subtitle="Click any ticket on the left to open the thread." /></div>
          ) : (
            <div className="flex flex-col h-[70vh]">
              <div className="px-5 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">{sel.number} · {sel.customer_name}</div>
                  <div className="text-[16px] font-semibold truncate">{sel.subject}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusPill variant={ticketStatusVariant(sel.status)}>{sel.status.replace("_", " ")}</StatusPill>
                  {user?.role !== "customer" && (
                    <>
                      <Select value={sel.status} onChange={(e) => setStatus(e.target.value)} className="w-[140px] h-8 text-[12px]" data-testid="ticket-status-select">
                        <option value="open">open</option>
                        <option value="in_progress">in progress</option>
                        <option value="resolved">resolved</option>
                        <option value="closed">closed</option>
                      </Select>
                      <button onClick={deleteTicket} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-hover))] text-[hsl(var(--text-muted))] hover:text-rose-600 transition-colors" title="Delete ticket" data-testid="delete-ticket-btn">
                        <Trash2 className="w-4 h-4" strokeWidth={1.6}/>
                      </button>
                    </>
                  )}
                </div>
              </div>
              <div className="flex-1 overflow-auto p-5 space-y-4 bg-[hsl(var(--background))]">
                {sel.messages?.map((m, i) => {
                  const mine = m.author === user?.name;
                  const roleLabel = m.author_role || (mine ? "You" : "");
                  return (
                    <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-[13px] leading-relaxed ${mine ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--surface))] border border-[hsl(var(--border))]"}`}>
                        <div className={`text-[10px] uppercase tracking-widest mb-1 ${mine ? "text-white/80" : "text-[hsl(var(--text-muted))]"}`}>
                          <span className="font-semibold">{m.author}</span>
                          {roleLabel && <span className="opacity-80"> · {roleLabel}</span>}
                          <span className="opacity-70"> · {new Date(m.created_at).toLocaleString()}</span>
                        </div>
                        <div className="whitespace-pre-wrap">{m.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-4 border-t border-[hsl(var(--border))] flex gap-2">
                <Input placeholder="Type a reply…" value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") sendReply(); }} data-testid="ticket-reply-input"/>
                <Button onClick={sendReply} data-testid="send-reply"><Send className="w-4 h-4" strokeWidth={2}/>Send</Button>
              </div>
            </div>
          )}
        </Card>
      </div>

      {showNew && <NewTicketModal onClose={() => setShowNew(false)} onCreated={load} />}
    </div>
  );
}

function NewTicketModal({ onClose, onCreated }) {
  const { user } = useAuth();
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("Normal");
  const [message, setMessage] = useState("");
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState(user?.customer_id || "");
  const [internal, setInternal] = useState(false);
  const [category, setCategory] = useState("bug");
  const [projects, setProjects] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [saving, setSaving] = useState(false);
  const isStaff = user?.role !== "customer";

  useEffect(() => {
    if (isStaff) {
      api.get("/customers").then(({ data }) => setCustomers(data || []));
    }
  }, [isStaff]);

  // Load projects/invoices scoped to the picked customer (or all when internal)
  useEffect(() => {
    if (!isStaff) {
      // Customer view: their own projects + invoices
      Promise.all([api.get("/projects"), api.get("/invoices")]).then(([p, i]) => {
        setProjects(p.data || []); setInvoices(i.data || []);
      });
      return;
    }
    if (internal) {
      // Internal: don't load customer-scoped lists
      setProjects([]); setInvoices([]);
      setProjectId(""); setInvoiceId("");
      return;
    }
    if (!customerId) {
      setProjects([]); setInvoices([]);
      return;
    }
    Promise.all([
      api.get("/projects").then(({ data }) => (data || []).filter((p) => p.customer_id === customerId)),
      api.get("/invoices").then(({ data }) => (data || []).filter((i) => i.customer_id === customerId)),
    ]).then(([p, i]) => { setProjects(p); setInvoices(i); });
  }, [isStaff, customerId, internal]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/tickets", {
        subject,
        priority,
        message,
        customer_id: internal || !customerId ? undefined : customerId,
        internal: internal || false,
        category: internal ? category : undefined,
        project_id: projectId || undefined,
        invoice_id: invoiceId || undefined,
      });
      toast.success(internal ? "Internal ticket created" : "Ticket created");
      await onCreated();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create ticket");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <form onSubmit={save} className="w-[min(560px,100%)] max-h-[92vh] overflow-y-auto bg-[hsl(var(--surface))] rounded-2xl border border-[hsl(var(--border))] shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="new-ticket-modal">
        <div className="px-6 py-4 border-b border-[hsl(var(--border))] flex items-center justify-between sticky top-0 bg-[hsl(var(--surface))] z-10">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[hsl(var(--text-muted))]">New</div>
            <h2 className="text-[18px] font-bold">{internal ? "Internal ticket" : "Support ticket"}</h2>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-[hsl(var(--surface-hover))]"><X className="w-4 h-4" strokeWidth={1.6}/></button>
        </div>
        <div className="p-6 space-y-3">
          {isStaff && (
            <label className="flex items-start gap-3 p-3 rounded-lg border border-[hsl(var(--border))] hover:border-[hsl(var(--text-muted))] cursor-pointer" data-testid="internal-toggle">
              <input
                type="checkbox"
                checked={internal}
                onChange={(e) => setInternal(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[hsl(var(--primary))]"
              />
              <span className="flex-1">
                <span className="block text-[12.5px] font-semibold">Internal ticket</span>
                <span className="block text-[11px] text-[hsl(var(--text-muted))] mt-0.5 leading-snug">Soubor One bug, staff request, or anything not tied to a specific customer. Customers will never see this.</span>
              </span>
            </label>
          )}

          {isStaff && !internal && (
            <div>
              <Label>Customer *</Label>
              <Select value={customerId} onChange={(e) => setCustomerId(e.target.value)} required={!internal} data-testid="ticket-customer-picker">
                <option value="">Select a customer</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.company}</option>)}
              </Select>
            </div>
          )}

          {isStaff && internal && (
            <div>
              <Label>Category</Label>
              <Select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="internal-category">
                <option value="bug">Bug · something's broken in Soubor One</option>
                <option value="request">Request · feature, change, idea</option>
                <option value="platform">Platform · infra, deploy, env</option>
                <option value="other">Other</option>
              </Select>
            </div>
          )}

          <div><Label>Subject *</Label><Input required value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="ticket-subject"/></div>
          <div>
            <Label>Priority</Label>
            <Select value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option>Low</option><option>Normal</option><option>High</option><option>Urgent</option>
            </Select>
          </div>

          {/* Optional cross-links */}
          {!internal && (projects.length > 0 || invoices.length > 0) && (
            <div className="grid grid-cols-2 gap-3">
              {projects.length > 0 && (
                <div>
                  <Label>Link to project (optional)</Label>
                  <Select value={projectId} onChange={(e) => setProjectId(e.target.value)} data-testid="ticket-project-picker">
                    <option value="">Don't link</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Select>
                </div>
              )}
              {invoices.length > 0 && (
                <div>
                  <Label>Link to invoice (optional)</Label>
                  <Select value={invoiceId} onChange={(e) => setInvoiceId(e.target.value)} data-testid="ticket-invoice-picker">
                    <option value="">Don't link</option>
                    {invoices.map((i) => <option key={i.id} value={i.id}>{i.number} · {i.status}</option>)}
                  </Select>
                </div>
              )}
            </div>
          )}

          <div><Label>Message *</Label><Textarea required value={message} onChange={(e) => setMessage(e.target.value)} rows={5} data-testid="ticket-message"/></div>
        </div>
        <div className="px-6 py-4 border-t border-[hsl(var(--border))] flex justify-end gap-2 bg-[hsl(var(--background))] sticky bottom-0">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="create-ticket">{saving ? "Creating…" : (internal ? "Create internal ticket" : "Create ticket")}</Button>
        </div>
      </form>
    </div>
  );
}
